import { Text } from "@solomon-labs/design/components/ui";
import React from "react";

type FieldRowVariant = "default" | "detail";

interface FieldRowProps {
    /** Left side — label text or custom node. */
    label: React.ReactNode;
    /** Right side — value text or custom node. */
    value?: React.ReactNode;
    /**
     * Visual weight.
     * - `default` — `text-base` label, `text-base` numeric value. Used for field
     *   headers ("You Send"), stat rows ("APY · 9.3%"), and projections.
     * - `detail` — `text-sm` throughout. Used for fee/info rows
     *   ("Price Impact · —", "Network Cost · $0.01").
     */
    variant?: FieldRowVariant;
    /** Optional className on the outer flex container. */
    className?: string;
}

/**
 * @version 1.0.0
 * @published 2026-04-09
 * @history
 *   1.0.0 — Initial publish. Generic label + value row used across
 *           transaction forms, fee breakdowns, and stat displays.
 */
export function FieldRow({
    label,
    value,
    variant = "default",
    className
}: FieldRowProps): React.ReactElement {
    const labelVariant = variant === "detail" ? "small" : "body";

    return (
        <div className={`flex items-center justify-between ${className ?? ""}`}>
            <Text
                variant={labelVariant}
                render={<span />}
                className="text-foreground-neutral"
            >
                {label}
            </Text>
            {value !== undefined && (
                <Text variant="number" className="text-foreground-base">
                    {value}
                </Text>
            )}
        </div>
    );
}
